import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, TrendingUp, Plus, Eye, MapPin, Users, DollarSign, RotateCcw, BarChart3, PieChart, Bell } from 'lucide-react';
import MainService from '../services/mainservice';
import Notifications from './Notifications';

const Dashboard = () => {
  const navigate = useNavigate();
  const [portalCounters, setPortalCounters] = useState({});
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setRecentDeliveries([]);

      // Get token from localStorage
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        setError('No authentication token found');
        return;
      }

      const userData = JSON.parse(tokenData);
      const token = userData.Token;

      // Fetch all API endpoints
      const [portalCountersResponse, recentDeliveriesResponse] = await Promise.all([
        MainService.GetPortalDashCounters(token),
        MainService.RecentDeliveriesInPortal(userData.ClientId, token)
      ]);

      setPortalCounters(portalCountersResponse || {});
      setRecentDeliveries(recentDeliveriesResponse || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Network Error - Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Dashboard stats based on portal counters API data
  const stats = [
    {
      title: 'Total Customers',
      value: portalCounters.TotalCustomers || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Users,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Total Orders',
      value: portalCounters.TotalOrders || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Truck,
      color: 'from-primary-500 to-primary-600'
    },
    {
      title: 'Today\'s Orders',
      value: portalCounters.TodaysOrders || '0',
      change: '+0%',
      changeType: 'positive',
      icon: Package,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Today Delivered',
      value: portalCounters.TodayDelivered || '0',
      change: '+0%',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Total Client Money',
      value: portalCounters.TotalClientMoney || '0',
      change: '+0%',
      changeType: 'neutral',
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: 'Total Client Returns',
      value: portalCounters.TotalClientReturns || '0',
      change: '+0%',
      changeType: 'neutral',
      icon: RotateCcw,
      color: 'from-orange-500 to-orange-600'
    }
  ];


  const getStatusColor = (status) => {
    if (!status || typeof status !== 'string') return 'text-gray-600 bg-gray-100';

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'in transit':
      case 'intransit':
        return 'text-primary-600 bg-primary-100';
      case 'out for delivery':
      case 'outfordelivery':
        return 'text-blue-600 bg-blue-100';
      case 'delayed':
        return 'text-orange-600 bg-orange-100';
      case 'cancelled':
      case 'canceled':
        return 'text-red-600 bg-red-100';
      case 'returned':
        return 'text-yellow-600 bg-yellow-100';
      case 'processing':
        return 'text-purple-600 bg-purple-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="gradient-bg page-container">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-primary-600">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gradient-bg page-container">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <Clock className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gradient-bg page-container">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600">Welcome back! Here's what's happening with your deliveries.</p>
          </div>
          <button
            onClick={() => setShowNotifications(true)}
            className="relative p-3 bg-white rounded-xl shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors duration-200 group"
            title="Notifications"
          >
            <Bell className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
            {/* Notification badge - you can add logic to show count if needed */}
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
          </button>
        </div>

        {/* Stats Cards - 6 cards in 2 rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="gradient-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' :
                        stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                        {stat.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">from last month</span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         
          {/* Pickup/Delivery Status - Donut Chart */}
          <div className="gradient-card">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pickup / Delivery Status (18/09/2025)</h2>
            </div>
            <div className="h-64 flex items-center justify-center">
              <div className="text-center w-full">
                <div className="relative w-48 h-48 mx-auto">
                  {(() => {
                    const todayStats = [
                      { name: "Today's Orders", value: portalCounters.TodaysOrders || 0, color: '#3B82F6' },
                      { name: 'Today Processing', value: portalCounters.TodayProcessing || 0, color: '#EAB308' },
                      { name: 'Today Delivered', value: portalCounters.TodayDelivered || 0, color: '#10B981' },
                      { name: 'Today Returned', value: portalCounters.TodayReturned || 0, color: '#F97316' },
                      { name: 'Today Cancelled', value: portalCounters.TodayCancelled || 0, color: '#EF4444' }
                    ];

                    const total = todayStats.reduce((sum, stat) => sum + stat.value, 0);
                    
                    if (total === 0) {
                      return (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-gray-500 text-sm">No Data</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    const radius = 60;
                    const innerRadius = 40;
                    let currentAngle = 0;

                    return (
                      <svg width="192" height="192" className="transform -rotate-90">
                        {todayStats.map((stat, index) => {
                          if (stat.value === 0) return null;
                          
                          const percentage = stat.value / total;
                          const angle = percentage * 360;
                          const startAngle = currentAngle;
                          const endAngle = currentAngle + angle;
                          
                          const x1 = 96 + radius * Math.cos((startAngle * Math.PI) / 180);
                          const y1 = 96 + radius * Math.sin((startAngle * Math.PI) / 180);
                          const x2 = 96 + radius * Math.cos((endAngle * Math.PI) / 180);
                          const y2 = 96 + radius * Math.sin((endAngle * Math.PI) / 180);
                          
                          const innerX1 = 96 + innerRadius * Math.cos((startAngle * Math.PI) / 180);
                          const innerY1 = 96 + innerRadius * Math.sin((startAngle * Math.PI) / 180);
                          const innerX2 = 96 + innerRadius * Math.cos((endAngle * Math.PI) / 180);
                          const innerY2 = 96 + innerRadius * Math.sin((endAngle * Math.PI) / 180);
                          
                          const largeArcFlag = angle > 180 ? 1 : 0;
                          
                          const pathData = [
                            `M ${x1} ${y1}`,
                            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                            `L ${innerX2} ${innerY2}`,
                            `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerX1} ${innerY1}`,
                            'Z'
                          ].join(' ');
                          
                          currentAngle += angle;
                          
                          return (
                            <path
                              key={index}
                              d={pathData}
                              fill={stat.color}
                              className="hover:opacity-80 transition-opacity duration-200"
                            />
                          );
                        })}
                      </svg>
                    );
                  })()}

                  {/* Center Circle for Donut Effect */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {portalCounters.TodaysOrders || 0}
                      </div>
                      <div className="text-xs text-gray-600">Total Orders</div>
                    </div>
                  </div>
                </div>

                {/* Legend for Pickup/Delivery Status */}
                <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-gray-700">Today's Orders: {portalCounters.TodaysOrders || 0}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                    <span className="text-gray-700">Processing: {portalCounters.TodayProcessing || 0}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-700">Delivered: {portalCounters.TodayDelivered || 0}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-orange-500 rounded"></div>
                    <span className="text-gray-700">Returned: {portalCounters.TodayReturned || 0}</span>
                  </div>
                  <div className="flex items-center space-x-2 col-span-2 justify-center">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span className="text-gray-700">Cancelled: {portalCounters.TodayCancelled || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Statistics - New Chart */}
          <div className="gradient-card">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Delivery Statistics</h2>
            </div>
            <div className="h-64 flex items-center justify-center">
              <div className="text-center w-full">
                {/* Bar Chart - Always visible */}
                <div className="h-48 bg-white border border-gray-200 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="w-full h-full flex items-end justify-around px-4 pb-4">
                    {/* Delivery Statistics from portalCounters */}
                    {(() => {
                      const deliveryStats = [
                        {
                          name: 'TotalOrders',
                          value: portalCounters.TotalOrders || 0,
                          color: 'bg-blue-500',
                          height: 0
                        },
                        {
                          name: 'TotalProcessing',
                          value: portalCounters.TotalProcessing || 0,
                          color: 'bg-yellow-500',
                          height: 0
                        },
                        {
                          name: 'TotalDelivered',
                          value: portalCounters.TotalDelivered || 0,
                          color: 'bg-green-500',
                          height: 0
                        },
                        {
                          name: 'TotalCancelled',
                          value: portalCounters.TotalCancelled || 0,
                          color: 'bg-red-500',
                          height: 0
                        },
                        {
                          name: 'TotalReturned',
                          value: portalCounters.TotalReturned || 0,
                          color: 'bg-orange-500',
                          height: 0
                        }
                      ];

                      // Calculate heights - ensure minimum height for visibility
                      const maxValue = Math.max(...deliveryStats.map(stat => stat.value));
                      deliveryStats.forEach(stat => {
                        if (maxValue > 0) {
                          stat.height = Math.max((stat.value / maxValue) * 100, 20); // Minimum 20px height
                        } else {
                          stat.height = 20; // Default height when no data
                        }
                      });

                      return deliveryStats.map((stat, index) => (
                        <div key={index} className="flex flex-col items-center justify-end h-full">
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 ${stat.color} rounded-t transition-all duration-500 hover:opacity-80 flex items-center justify-center`}
                              style={{ height: `${stat.height}px` }}
                              title={`${stat.name}: ${stat.value}`}
                            >
                              <span className="text-white font-bold text-xs">{stat.value}</span>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                </div>

                {/* Legend for Delivery Statistics */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span className="text-gray-700">TotalOrders</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                    <span className="text-gray-700">TotalProcessing</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-700">TotalDelivered</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                    <span className="text-gray-700">TotalCancelled</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-orange-500 rounded"></div>
                    <span className="text-gray-700">TotalReturned</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's Status - Table */}
          <div className="gradient-card">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Today's Status</h2>
            </div>
            <div className="h-64">
              <table className="w-full">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-2">
                      <p className="text-sm font-medium text-gray-700">Today's Orders</p>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-lg font-bold text-blue-600">{portalCounters.TodaysOrders || 0}</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-2">
                      <p className="text-sm font-medium text-gray-700">Today Processing</p>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-lg font-bold text-yellow-600">{portalCounters.TodayProcessing || 0}</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-2">
                      <p className="text-sm font-medium text-gray-700">Today Delivered</p>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-lg font-bold text-green-600">{portalCounters.TodayDelivered || 0}</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-2">
                      <p className="text-sm font-medium text-gray-700">Today Returned</p>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-lg font-bold text-orange-600">{portalCounters.TodayReturned || 0}</span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-2">
                      <p className="text-sm font-medium text-gray-700">Today Cancelled</p>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="text-lg font-bold text-red-600">{portalCounters.TodayCancelled || 0}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Deliveries */}
        <div className="mt-8 gradient-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recent Deliveries</h2>
            <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">VOUCHER NO</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CLIENT BILL</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY DATE</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CUSTOMER</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY ADDRESS</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentDeliveries.length > 0 ? (
                  recentDeliveries.map((delivery, index) => (
                    <tr key={delivery.Id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                      <td className="py-3 px-4 text-center text-sm text-gray-500 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">
                        {delivery.VoucherNo}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {delivery.ClientBillNo}
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const status = delivery.Status || delivery.status;
                          let statusValue = 'N/A';
                          if (typeof status === 'object' && status !== null) {
                            statusValue = status.Name || status.name || 'N/A';
                          } else if (typeof status === 'string') {
                            statusValue = status;
                          }
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(statusValue)}`}>
                              {statusValue}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {delivery.DeliveryDate ? new Date(delivery.DeliveryDate).toLocaleDateString('en-GB') : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {delivery.Customer?.Name || 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {delivery.CustomerAddress?.Emirate?.Name || 'N/A'} |
                        {delivery.CustomerAddress?.Location?.Name || 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/order-details/${delivery.Id || delivery.id}`)}
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-gray-500">
                      No recent deliveries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Notifications Modal */}
      <Notifications 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />
    </div>
  );
};

export default Dashboard; 